import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import DataTableController from 'components/common/dataTable/dataTableController'
import {
  InventoryDataTable,
  columns,
  InventoryFilterForm,
} from 'components/inventory'
import { usePageAccess, useQuery } from 'hooks'
import {
  downloadErpResetBarcodes,
  downloadInventory,
  importErpQtyResetBarcodes,
  importInventory,
  uploadInventoryBarcodeSearchFile,
} from 'utils/api/inventoryApi'
import toast from 'react-hot-toast'
import { FILTER_FIELD_INVENTORY } from 'components/inventory/inventoryFilterForm'
import { filterEmptyValues } from 'utils/helpers'
import { AxiosError } from 'axios'
import DataTableImportButton from 'components/common/dataTable/dataTableImportButton'
import { Button } from 'components/common'
import ActiveFilterChip from 'components/common/activeFilterChip'
import BarcodeSearch from 'components/common/barcodeSearch'
import {
  BARCODE_COUNT_KEY,
  BARCODE_ERROR_MESSAGES,
  BARCODE_QUERY_KEY,
  BARCODE_SEARCH_EXPIRED,
  BARCODE_TOKEN_KEY,
  getBarcodeErrorCode,
} from 'components/common/barcodeSearch/constants'
import { ExportIcon } from 'components/icons'
import { ImportColumn } from 'components/common/importModal'
import { downloadImportSample, getImportTemplate } from 'utils/api/importApi'
import useModal from 'hooks/useModal'
import ErpWindowUpdateModal from 'components/inventory/erpWindowUpdateModal'

const Inventory = () => {
  const [showFilter, setShowFilter] = useState(false)
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [importColumns, setImportColumns] = useState<ImportColumn[]>([])
  const [erpResetColumns, setErpResetColumns] = useState<ImportColumn[]>([])
  const { isEditor } = usePageAccess()
  const {
    isOpen: isErpWindowModalOpen,
    openModal: openErpWindowModal,
    closeModal: closeErpWindowModal,
  } = useModal()

  const { getQuery, setMultipleQueries } = useQuery()

  useEffect(() => {
    getImportTemplate('inventory')
      .then((res) => setImportColumns(res.data.columns))
      .catch(() => {})
  }, [])

  const handleDownloadSample = async () => {
    const response = await downloadImportSample('inventory')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'inventory_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  useEffect(() => {
    getImportTemplate('erpQtyResetBarcodes')
      .then((res: { data: { columns: ImportColumn[] } }) =>
        setErpResetColumns(res.data.columns),
      )
      .catch(() => {})
  }, [])

  const handleDownloadErpResetSample = async () => {
    const response = await downloadImportSample('erpQtyResetBarcodes')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'erp_qty_reset_barcodes_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const toggleFilterForm = () => {
    setShowFilter(!showFilter)
  }

  // Will handle the file export funtionality with toast
  const handleExport = async () => {
    const filters = getQuery(FILTER_FIELD_INVENTORY)
    const barcodeToken = getQuery(BARCODE_TOKEN_KEY)
    const barcode = getQuery(BARCODE_QUERY_KEY)

    // filtering empty values and keys of filter
    const pricingFilter = filters ? filterEmptyValues(filters) : {}

    // A barcode search on its own is enough to scope the export; otherwise at
    // least one filter is required.
    if (!barcodeToken && !barcode && Object.keys(pricingFilter).length <= 0) {
      return toast.error('Please select at least on filter to export data!', {
        duration: 3000,
      })
    }

    const loadingToast = toast.loading('Loading...', { duration: Infinity })

    try {
      await downloadInventory({ filters: pricingFilter, barcodeToken, barcode })
      toast.success('File will be sent to you in email!', {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      // Token expired mid-flight → clear the search and prompt a re-upload.
      if (getBarcodeErrorCode(error) === BARCODE_SEARCH_EXPIRED) {
        setMultipleQueries({
          [BARCODE_TOKEN_KEY]: undefined,
          [BARCODE_COUNT_KEY]: undefined,
          page: 1,
        })
        return toast.error(BARCODE_ERROR_MESSAGES[BARCODE_SEARCH_EXPIRED], {
          id: loadingToast,
          duration: 4000,
        })
      }
      if (error instanceof AxiosError) {
        if (error?.response?.data?.errors?.msg?.runningService) {
          return toast.error(error.response.data.errors.msg.runningService, {
            id: loadingToast,
            duration: 3000,
          })
        }
      }
      toast.error('Failed to export Inventory Data.', {
        id: loadingToast,
        duration: 3000,
      })
    }
  }

  const handleErpQtyResetExport = async () => {
    const loadingToast = toast.loading('Loading...', { duration: Infinity })

    try {
      await downloadErpResetBarcodes()
      toast.success('File will be sent to you in email!', {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data?.errors?.msg?.runningService) {
          return toast.error(error.response.data.errors.msg.runningService, {
            id: loadingToast,
            duration: 3000,
          })
        }
      }
      toast.error('Failed to export Inventory Data.', {
        id: loadingToast,
        duration: 3000,
      })
    }
  }

  return (
    <>
      <Helmet>
        <title>GAG Aggregator | Inventory</title>
      </Helmet>
      {/* Table Header Controlls : Import | Export | column manager | search */}
      <DataTableController
        isEditor={isEditor}
        columns={columns}
        handleFilter={toggleFilterForm}
        showFilter={showFilter}
        handleExport={handleExport}
        handleImport={importInventory}
        activeColumnKey="inventory"
        defaultSelectedColumns={selectedColumns}
        placeholder="Search by barcode and store id"
        loading={loading}
        importColumns={importColumns}
        importModuleName="inventory"
        onDownloadSample={handleDownloadSample}
      />
      <div className="mb-5 flex  items-center gap-3">
        <DataTableImportButton
          label="Import Erp Qty Reset Barcodes"
          disabled={loading}
          uploadApiFunction={importErpQtyResetBarcodes}
          className="  border border-gray-300"
          importColumns={erpResetColumns}
          importModuleName="ERP qty reset barcodes"
          onDownloadSample={handleDownloadErpResetSample}
        />
        <Button
          type="button"
          buttonText="Export Erp Qty Reset Barcodes"
          variant="custom"
          iconClassName="text-dataTableIcon"
          icon={<ExportIcon />}
          className="rounded-lg border border-gray-300 px-3.5 py-2 text-sm font-semibold text-sidebar-primary hover:bg-gray-200"
          onClick={handleErpQtyResetExport}
          disabled={loading}
        />
        {isEditor && (
          <Button
            type="button"
            buttonText="ERP Window Update by Stores"
            variant="custom"
            className="rounded-lg border border-gray-300 px-3.5 py-2 text-sm font-semibold text-sidebar-primary hover:bg-gray-200"
            onClick={openErpWindowModal}
            disabled={loading}
          />
        )}
      </div>

      <ErpWindowUpdateModal
        isOpen={isErpWindowModalOpen}
        closeModal={closeErpWindowModal}
      />

      <BarcodeSearch uploadFile={uploadInventoryBarcodeSearchFile} />

      {!showFilter && <ActiveFilterChip filterKey={FILTER_FIELD_INVENTORY} />}

      {showFilter ? (
        <InventoryFilterForm toggleFilterForm={toggleFilterForm} />
      ) : (
        <InventoryDataTable
          loading={loading}
          setLoading={setLoading}
          setSelectedColumns={setSelectedColumns}
          isEditor={isEditor}
        />
      )}
    </>
  )
}

export default Inventory
